export type PickRatingTone = 'positive' | 'negative' | 'neutral' | 'warning' | 'context';

export interface PickRatingBadge {
  label: string;
  tone: PickRatingTone;
  title: string;
}

export interface DraftPickRating {
  draftDay?: PickRatingBadge;
  outcome?: PickRatingBadge;
  context?: PickRatingBadge;
}

export interface DraftPickRatingInput {
  preRank?: number;
  draftedRank: number;
  finalRank?: number;
  finalPpgRank?: number;
  gamesPlayed?: number;
  healthThreshold: number;
  crossPosition?: boolean;
}

const toleranceFor = (rank: number) => Math.max(2, Math.round(rank * 0.2));

const formatNumber = (value: number) => (
  Number.isInteger(value) ? value.toString() : value.toFixed(1)
);

const draftDayRating = (preRank: number, draftedRank: number): PickRatingBadge => {
  const tolerance = toleranceFor(preRank);
  const difference = draftedRank - preRank;
  const detail = `PRE ${formatNumber(preRank)} vs DRAFTED ${formatNumber(draftedRank)}; tolerance ${tolerance}.`;

  if (difference <= -2 * tolerance) {
    return { label: 'Big Reach', tone: 'negative', title: `Drafted much earlier than the pre-draft rank. ${detail}` };
  }
  if (difference <= -tolerance) {
    return { label: 'Reach', tone: 'warning', title: `Drafted earlier than the pre-draft rank. ${detail}` };
  }
  if (difference >= 2 * tolerance) {
    return { label: 'Big Value', tone: 'positive', title: `Drafted much later than the pre-draft rank. ${detail}` };
  }
  if (difference >= tolerance) {
    return { label: 'Value', tone: 'positive', title: `Drafted later than the pre-draft rank. ${detail}` };
  }
  return { label: 'On Board', tone: 'neutral', title: `Drafted close to the pre-draft rank. ${detail}` };
};

const outcomeRating = (input: DraftPickRatingInput): PickRatingBadge | undefined => {
  if (!input.finalRank) {
    return undefined;
  }

  const expectedRank = input.preRank
    ? (input.preRank + input.draftedRank) / 2
    : input.draftedRank;
  const tolerance = toleranceFor(expectedRank);
  const improvement = expectedRank - input.finalRank;
  const expectedDetail = input.preRank
    ? `Expected ${formatNumber(expectedRank)} from PRE ${formatNumber(input.preRank)} and DRAFTED ${formatNumber(input.draftedRank)}.`
    : `Expected ${formatNumber(expectedRank)} from DRAFTED ${formatNumber(input.draftedRank)} because PRE was unavailable.`;
  const detail = `${expectedDetail} FINAL ${formatNumber(input.finalRank)}; tolerance ${tolerance}.`;

  let rating: PickRatingBadge;
  if (improvement >= 2 * tolerance) {
    rating = { label: 'Smash', tone: 'positive', title: `Final rank greatly beat expectation. ${detail}` };
  } else if (improvement >= tolerance) {
    rating = { label: 'Hit', tone: 'positive', title: `Final rank beat expectation. ${detail}` };
  } else if (improvement <= -2 * tolerance) {
    rating = { label: 'Bust', tone: 'negative', title: `Final rank finished well behind expectation. ${detail}` };
  } else if (improvement <= -tolerance) {
    rating = { label: 'Miss', tone: 'warning', title: `Final rank finished behind expectation. ${detail}` };
  } else {
    rating = { label: 'Expected', tone: 'neutral', title: `Final rank was close to expectation. ${detail}` };
  }

  const limitedGames = typeof input.gamesPlayed === 'number'
    && input.gamesPlayed < input.healthThreshold;
  const underperformed = rating.label === 'Miss' || rating.label === 'Bust';
  if (!limitedGames || !underperformed) {
    return rating;
  }

  if (!input.finalPpgRank) {
    return {
      label: 'Limited',
      tone: 'context',
      title: `${input.gamesPlayed} games played and no PPG rank was available. ${detail}`,
    };
  }

  if (input.finalPpgRank <= expectedRank + tolerance) {
    return {
      label: 'Bad Luck',
      tone: 'context',
      title: `${input.gamesPlayed} games played, but PPG rank ${input.finalPpgRank} remained near expectation. ${detail}`,
    };
  }

  return rating;
};

export const getDraftPickRating = (input: DraftPickRatingInput): DraftPickRating => {
  if (input.crossPosition) {
    return {
      context: {
        label: 'Cross-Pos',
        tone: 'context',
        title: 'No rating: PRE, DRAFTED, and FINAL do not use the same normalized position group.',
      },
    };
  }

  return {
    draftDay: input.preRank ? draftDayRating(input.preRank, input.draftedRank) : undefined,
    outcome: outcomeRating(input),
  };
};
